/**
 * Key Resolver
 * Resolves a clicked element to its translation key and ContentUnit.
 */

import type { ContentUnit } from '../../content/types.js';
import { parseBlockElement } from '../markup-parser.js';
import { generateAutoKey } from '../md5.js';

const BLOCK_SELECTOR =
  'p,h1,h2,h3,h4,h5,h6,li,td,th,label,button,figcaption,dt,dd,summary,caption';

export interface ResolvedKey {
  key: string;
  /** Source ContentUnit (original text with indexed tags) */
  unit: ContentUnit;
  /** Current translation as ContentUnit (if element is already translated) */
  translatedUnit: ContentUnit | null;
  element: Element;
}

/**
 * Resolve an element to its translation key.
 * Priority: data-lr-block attr → parseBlockElement → fallback textContent
 */
export function resolveKey(target: Element): ResolvedKey | null {
  // Priority 1: Already translated element with data-lr-block
  const blockEl = target.closest('[data-lr-block]');
  if (blockEl) {
    const key = blockEl.getAttribute('data-lr-block')!;
    const original = blockEl.getAttribute('data-lr-original');
    let unit: ContentUnit;
    let translatedUnit: ContentUnit | null = null;

    if (original) {
      // Parse original HTML → source ContentUnit
      const temp = document.createElement(blockEl.tagName);
      temp.innerHTML = original;
      unit = parseBlockElement(temp);
      // Parse current DOM → translated ContentUnit
      translatedUnit = parseBlockElement(blockEl);
    } else {
      unit = parseBlockElement(blockEl);
    }
    return { key, unit, translatedUnit, element: blockEl };
  }

  // Priority 2: Find nearest block element and parse it
  const nearest = target.closest(BLOCK_SELECTOR);
  if (nearest) {
    const unit = parseBlockElement(nearest);
    const key = generateAutoKey(unit.text);
    return { key, unit, translatedUnit: null, element: nearest };
  }

  // Priority 3: Fallback to element text
  const text = target.textContent?.trim();
  if (text && text.length >= 2) {
    const key = generateAutoKey(text);
    const unit: ContentUnit = { text, styles: {} };
    return { key, unit, translatedUnit: null, element: target };
  }

  return null;
}
